class FaqHighlightComponent extends HTMLElement {
  #controller = new AbortController();

  connectedCallback() {
    this.#controller = new AbortController();

    const { signal } = this.#controller;

    for (const details of this.#detailsElements) {
      details.addEventListener('toggle', this.#handleToggle, { signal });
    }

    this.#syncInitialState();
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  get #detailsElements() {
    return Array.from(this.querySelectorAll('.faq-highlight__details')).filter(
      (details) => details instanceof HTMLDetailsElement,
    );
  }

  get #mediaElements() {
    return Array.from(this.querySelectorAll('.faq-highlight__media-image, .faq-highlight__media-placeholder')).filter(
      (element) => element instanceof HTMLElement,
    );
  }

  get #defaultImageIndex() {
    return Number.parseInt(this.dataset.defaultImageIndex || '0', 10) || 0;
  }

  get #isDynamicImageMode() {
    return this.dataset.imageMode !== 'fixed';
  }

  #handleToggle = (event) => {
    const currentDetails = event.currentTarget;

    if (!(currentDetails instanceof HTMLDetailsElement)) return;

    if (currentDetails.open) {
      this.#closeSiblings(currentDetails);

      if (this.#isDynamicImageMode) {
        this.#setActiveImage(this.#getImageIndex(currentDetails));
      }

      return;
    }

    if (this.#isDynamicImageMode && !this.#detailsElements.some((details) => details.open)) {
      this.#setActiveImage(this.#defaultImageIndex);
    }
  };

  #syncInitialState() {
    const openDetails = this.#detailsElements.filter((details) => details.open);

    if (openDetails.length > 1) {
      const [firstOpenDetails, ...otherOpenDetails] = openDetails;

      for (const details of otherOpenDetails) {
        details.open = false;
      }

      if (this.#isDynamicImageMode) {
        this.#setActiveImage(this.#getImageIndex(firstOpenDetails));
      }

      return;
    }

    if (openDetails.length === 1) {
      if (this.#isDynamicImageMode) {
        this.#setActiveImage(this.#getImageIndex(openDetails[0]));
      }

      return;
    }

    if (this.#isDynamicImageMode) {
      this.#setActiveImage(this.#defaultImageIndex);
    }
  }

  #closeSiblings(currentDetails) {
    for (const details of this.#detailsElements) {
      if (details === currentDetails || !details.open) continue;

      details.open = false;
    }
  }

  #getImageIndex(details) {
    return Number.parseInt(details.dataset.imageIndex || String(this.#defaultImageIndex), 10) || this.#defaultImageIndex;
  }

  #setActiveImage(imageIndex) {
    for (const element of this.#mediaElements) {
      const currentIndex = Number.parseInt(element.dataset.imageIndex || '-1', 10);
      const isActive = currentIndex === imageIndex;

      element.dataset.active = String(isActive);

      if (element instanceof HTMLImageElement) {
        element.setAttribute('aria-hidden', String(!isActive));
      }
    }
  }
}

if (!customElements.get('faq-highlight-component')) {
  customElements.define('faq-highlight-component', FaqHighlightComponent);
}